import { useEffect, useState } from "react";
import Lenis from "lenis";
import { MotionConfig, AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import "@/App.css";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Understanding from "@/components/Understanding";
import EmotionalCurve from "@/components/EmotionalCurve";
import AudienceSelector from "@/components/AudienceSelector";
import ConceptScreens from "@/components/ConceptScreens";
import Journeys from "@/components/Journeys";
import Architecture from "@/components/Architecture";
import BrandTerritory from "@/components/BrandTerritory";
import Scope from "@/components/Scope";
import Roadmap from "@/components/Roadmap";
import Investment from "@/components/Investment";
import Partnership from "@/components/Partnership";
import Trust from "@/components/Trust";
import WhyUs from "@/components/WhyUs";
import Closing, { MeetingPanel } from "@/components/Closing";
import ExecutiveOverview from "@/components/ExecutiveOverview";
import { LightboxProvider } from "@/components/Lightbox";

function StickyMobileCta({ onDiscuss }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > window.innerHeight * 1.15 && window.scrollY < document.body.scrollHeight - window.innerHeight * 2.2);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.button
          data-testid="sticky-mobile-cta"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.4 }}
          onClick={onDiscuss}
          className="sm:hidden fixed bottom-5 right-5 z-[70] inline-flex items-center gap-2 bg-[#FFD400] text-[#0F0F0F] text-[10px] font-bold uppercase tracking-[0.16em] px-5 py-3.5 rounded-full shadow-[0_12px_30px_-8px_rgba(0,0,0,0.45)] cursor-pointer"
        >
          Discuss the Project
          <ArrowUpRight size={12} strokeWidth={2.5} />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

export default function App() {
  const [panelOpen, setPanelOpen] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const overlayActive = panelOpen || overviewOpen;

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          if (id === "discuss") setPanelOpen(true);
          else el.scrollIntoView();
        }
      }, 600);
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = overlayActive ? "hidden" : "";
    if (window.__lenis) {
      overlayActive ? window.__lenis.stop() : window.__lenis.start();
    }
  }, [overlayActive]);

  return (
    <MotionConfig reducedMotion="user">
      <LightboxProvider>
        <div className="grain relative min-h-screen bg-[#F6F0E6]" data-testid="proposal-microsite">
          <Nav onDiscuss={() => setPanelOpen(true)} onOverview={() => setOverviewOpen(true)} />
          <main>
            <Hero onOverview={() => setOverviewOpen(true)} />
            <Marquee />
            <Understanding />
            <EmotionalCurve />
            <AudienceSelector />
            <ConceptScreens />
            <Journeys />
            <Architecture />
            <BrandTerritory />
            <Scope />
            <Roadmap />
            <Investment />
            <Partnership />
            <Trust />
            <WhyUs />
            <Closing onDiscuss={() => setPanelOpen(true)} />
          </main>
          <StickyMobileCta onDiscuss={() => setPanelOpen(true)} />
          <img
            src="/images/bsm-logo.png"
            alt=""
            aria-hidden="true"
            data-testid="corner-logo-mark"
            className="fixed bottom-5 left-5 z-[60] w-9 h-9 rounded-lg opacity-50 pointer-events-none select-none"
          />
          <ExecutiveOverview
            open={overviewOpen}
            onClose={() => setOverviewOpen(false)}
            onDiscuss={() => {
              setOverviewOpen(false);
              setTimeout(() => setPanelOpen(true), 300);
            }}
          />
          <MeetingPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
        </div>
      </LightboxProvider>
    </MotionConfig>
  );
}
